import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiShield, FiTruck, FiRefreshCw, FiCheckCircle } from 'react-icons/fi';
import api from '../services/api';
import LazyImage from '../components/ui/LazyImage';
import HoverImageScroll from '../components/ui/HoverImageScroll';
import SubscribeForm from '../components/ui/SubscribeForm';

// "What our customers say" section hidden for now
// const DEFAULT_CUSTOMER_REVIEWS = [
//   {
//     quote: 'Finally found a brand that truly understands fit and style for bigger sizes. Amazing quality and super comfortable.',
//     name: 'Rohit S.',
//   },
//   {
//     quote: 'The fabric, the fit, the design - everything is on point. BeyondXL is now my go-to shirts brand.',
//     name: 'Amit Verma',
//   },
//   {
//     quote: 'Great collections and fast delivery. Love the way these shirts make me look confident.',
//     name: 'Karan P.',
//   },
// ];

const isMeaningfulText = (value) => {
  if (!value || typeof value !== 'string') return false;
  const trimmed = value.trim();
  return trimmed.length > 0 && !/^[.\s•-]+$/.test(trimmed);
};

const formatCurrency = (amount) => {
  const value = Number(amount) || 0;
  return `₹${value.toLocaleString('en-IN')}`;
};

const getProductKey = (product, index, fallbackPrefix = 'product') => {
  if (product?.id) return String(product.id);
  if (product?._id) return String(product._id);
  if (product?.slug) return String(product.slug);
  return `${fallbackPrefix}-${index}`;
};

const FEATURE_ITEMS = [
  {
    icon: FiTruck,
    title: 'Designed for Indian Body Types',
    description: 'Made to fit what matters.',
  },
  {
    icon: FiCheckCircle,
    title: 'Premium Fabrics',
    description: 'Comfort you can feel.',
  },
  {
    icon: FiShield,
    title: 'Made to Last',
    description: 'Durable quality for everyday wear.',
  },
  {
    icon: FiRefreshCw,
    title: 'Easy Exchanges',
    description: 'Hassle-free returns and exchanges.',
  },
];

const Homepage = () => {
  const [collections, setCollections] = useState([]);
  const [products, setProducts] = useState([]);
  const [landingPageSettings, setLandingPageSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHomepageData = async () => {
      try {
        const [settingsResponse, collectionsResponse, productsResponse] = await Promise.allSettled([
          api.publicAPI.getLandingPageSettings(),
          api.publicAPI.getCollections({ light: true }),
          api.publicAPI.getFeaturedProducts()
        ]);

        if (settingsResponse.status === 'fulfilled' && settingsResponse.value.data?.success) {
          setLandingPageSettings(settingsResponse.value.data.settings || null);
        }

        if (collectionsResponse.status === 'fulfilled' && collectionsResponse.value.data?.success) {
          setCollections(collectionsResponse.value.data.collections || []);
        }

        if (productsResponse.status === 'fulfilled' && productsResponse.value.data?.success) {
          setProducts(productsResponse.value.data.products || []);
        }
      } catch (error) {
        console.error('Error loading homepage:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchHomepageData();
  }, []);

  const topCollections = useMemo(() => collections.slice(0, 4), [collections]);
  const bestSellers = useMemo(() => products.slice(0, 12), [products]);

  const showInitialLoader = false;

  if (showInitialLoader) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="relative w-20 h-20 mx-auto mb-5">
            <div className="absolute inset-0 rounded-full border-4 border-[#e4ac16]/30" />
            <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-[#e4ac16] animate-spin" />
            <div className="absolute inset-3 rounded-full bg-[#111111] animate-pulse" />
          </div>
          <p className="text-sm tracking-[0.22em] uppercase text-gray-700 font-semibold">Loading Styles</p>
        </div>
      </div>
    );
  }

  // Hero is intentionally static (no API call) for instant load.

  const storyLabel = isMeaningfulText(landingPageSettings?.story_label)
    ? landingPageSettings.story_label.trim()
    : 'Our Story';
  const storyHeading = isMeaningfulText(landingPageSettings?.story_heading)
    ? landingPageSettings.story_heading.trim()
    : 'Made for Every Plus Size. Made to Fit. Made to Stand Out.';
  const storyParagraph1 = isMeaningfulText(landingPageSettings?.story_paragraph_1)
    ? landingPageSettings.story_paragraph_1.trim()
    : 'BeyondXL was born out of a simple idea: everyone deserves clothing that fits well, looks great, and feels comfortable.';
  const storyParagraph2 = isMeaningfulText(landingPageSettings?.story_paragraph_2)
    ? landingPageSettings.story_paragraph_2.trim()
    : 'Started by a plus-size individual who experienced these challenges firsthand, BeyondXL was built with a clear goal to create fashion that combines style, comfort, and quality at a fair price.';
  const storyParagraph3 = isMeaningfulText(landingPageSettings?.story_paragraph_3)
    ? landingPageSettings.story_paragraph_3.trim()
    : 'Our vision is simple: to become the ultimate destination for plus-size fashion, offering everything from everyday essentials to statement styles in sizes 2XL to 12XL and beyond.';
  // "What our customers say" section hidden for now
  // const reviewsHeading = isMeaningfulText(landingPageSettings?.reviews_heading)
  //   ? landingPageSettings.reviews_heading.trim()
  //   : 'What our customers say';
  // const customerReviews = (landingPageSettings?.customer_reviews || DEFAULT_CUSTOMER_REVIEWS)
  //   .filter((review) => isMeaningfulText(review?.quote) && isMeaningfulText(review?.name));

  return (
    <div className="bg-white text-[#111111]">
      {/* Promo / coupon announcement bar (scrolling marquee) */}
      <section className="bg-black overflow-hidden py-3">
        <div className="flex w-max animate-marquee">
          {[0, 1].map((copy) => (
            <div
              key={copy}
              className="flex shrink-0 items-center"
              aria-hidden={copy === 1}
            >
              {Array.from({ length: 12 }).map((_, i) => (
                <div key={`${copy}-${i}`} className="flex shrink-0 items-center whitespace-nowrap">
                  <span className="text-[#e4ac16] text-xs sm:text-sm font-semibold tracking-wide px-8">
                    Use code <span className="font-bold">DIWALI</span> &mdash; BUY 2 GET 1 FREE
                  </span>
                  <span className="text-[#e4ac16]/50">&bull;</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </section>

      <section className="w-full">
        <div className="relative min-h-[360px] md:min-h-[520px] border-b border-gray-200 overflow-hidden">
          <img
            src="/Master Banner Diwali.png"
            alt="Diwali 2026 Offer - BeyondXL"
            className="absolute inset-0 w-full h-full object-cover object-center"
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />
        </div>
      </section>

      <section className="mt-5 bg-[#e4ac16] border-y border-[#cc960f]">
        <div className="max-w-[1260px] mx-auto px-4 py-3 text-center font-semibold tracking-wide text-[#1e1e1e]">
          EXPLORE YOUR STYLE
        </div>
      </section>

      <section className="max-w-[1260px] mx-auto px-4 md:px-6 py-6">
        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((item) => (
              <div key={item} className="aspect-[4/5] bg-gray-200 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {(topCollections.length ? topCollections : []).map((collection, index) => (
              <Link
                key={collection.id || `collection-${index}`}
                to={collection.slug ? `/collections/${collection.slug}` : '/collections'}
                className="group border border-gray-200"
              >
                <div className="relative aspect-[4/5] overflow-hidden bg-white">
                  <LazyImage
                    src={collection.image}
                    alt={collection.name || `Collection ${index + 1}`}
                    width={500}
                    height={625}
                    fit="cover"
                    className="group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-black/35 flex flex-col items-center justify-center text-white px-3 text-center">
                    <div className="uppercase text-lg tracking-wider font-semibold">{collection.name || 'Style'}</div>
                    <button className="mt-4 border border-white/70 text-xs px-3 py-1 hover:bg-white hover:text-black transition-colors">
                      Shop Collection
                    </button>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      <section className="max-w-[1260px] mx-auto px-4 md:px-6 py-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-[13px] font-bold tracking-wide text-gray-800">BEST SELLERS</h2>
          <Link to="/products" className="text-xs text-gray-600 hover:text-black">
            View All
          </Link>
        </div>

        <div className="flex gap-4 overflow-x-auto pb-3 scrollbar-thin">
          {loading ? (
            [1, 2, 3, 4].map((item) => (
              <div
                key={item}
                className="border border-gray-200 min-w-[220px] md:min-w-[260px] lg:min-w-[280px] snap-start"
              >
                <div className="relative aspect-[4/5] bg-gray-200 animate-pulse" />
                <div className="p-3 space-y-2">
                  <div className="h-3 bg-gray-200 rounded w-1/3 animate-pulse" />
                  <div className="h-4 bg-gray-200 rounded w-2/3 animate-pulse" />
                </div>
              </div>
            ))
          ) : (
            bestSellers.map((product, index) => (
            <Link
              key={getProductKey(product, index)}
              to={product.id ? `/products/${product.id}` : '/products'}
              className="group border border-gray-200 hover:shadow-sm transition-shadow min-w-[220px] md:min-w-[260px] lg:min-w-[280px] snap-start flex flex-col"
            >
              <HoverImageScroll
                images={product.images}
                alt={product.name || `Product ${index + 1}`}
              />
              <div className="p-3 text-left">
                <p className="text-xs uppercase text-[#d0a13a] mb-1">Best Seller</p>
                <h3 className="text-sm font-medium text-gray-900">{product.name}</h3>
                <p className="text-[11px] text-gray-500 uppercase mt-1">{product.collection_name || 'Classic'}</p>
                <p className="mt-2 font-bold text-base">{formatCurrency(product.price)}</p>
              </div>
            </Link>
            ))
          )}
        </div>
      </section>

      <section className="bg-[#e4ac16] mt-6">
        <div className="max-w-[1260px] mx-auto px-4 md:px-6 py-6 md:py-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {FEATURE_ITEMS.map(({ icon: Icon, title, description }) => (
              <div key={title} className="flex items-center justify-start lg:justify-center gap-4 min-w-0">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-black/10 flex items-center justify-center">
                  <Icon size={20} className="text-[#1e1e1e]" />
                </div>
                <div className="min-w-0 space-y-1">
                  <p className="font-semibold text-sm leading-snug text-[#1e1e1e]">{title}</p>
                  <p className="text-xs leading-relaxed text-[#1e1e1e]/80">{description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-[1260px] mx-auto px-4 md:px-6 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-[1.8fr_1fr] gap-6 items-stretch">
          <div className="bg-[#f6f6f6] p-6 md:p-8 border border-gray-200 flex flex-col justify-center">
            <h3 className="uppercase text-[#d0a13a] text-2xl font-serif mb-2">{storyLabel}</h3>
            <h4 className="text-2xl md:text-[30px] font-semibold leading-tight mb-4">
              {storyHeading}
            </h4>
            <p className="text-sm text-gray-700 mb-3">
              {storyParagraph1}
            </p>
            <p className="text-sm text-gray-700 mb-3">
              {storyParagraph2}
            </p>
            <p className="text-sm text-gray-700">
              {storyParagraph3}
            </p>
          </div>

          <div className="mt-6 lg:mt-0 overflow-hidden">
            <img
              src="/My Story Banner 540x420 (1).jpg"
              alt={storyLabel}
              loading="lazy"
              className="w-full h-full object-cover block"
            />
          </div>
        </div>
      </section>

      {/* "What our customers say" section hidden for now
      <section className="bg-[#f3f3f3] border-y border-gray-200 py-8">
        <div className="max-w-[1260px] mx-auto px-4 md:px-6">
          <h3 className="text-center text-2xl font-serif text-[#b8913a] mb-6">{reviewsHeading}</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {customerReviews.map((review) => (
              <article key={review.name} className="bg-white border border-gray-200 p-4 text-sm text-gray-700">
                <p>"{review.quote}"</p>
                <p className="mt-4 font-semibold text-gray-900">{review.name}</p>
              </article>
            ))}
          </div>
        </div>
      </section>
      */}

      <section className="bg-[#d9a11a] py-4">
        <div className="max-w-[1260px] mx-auto px-4 md:px-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h4 className="font-semibold text-lg">Stay in Style</h4>
            <p className="text-sm">Get updates on new arrivals, exclusive offers and style tips</p>
          </div>
          <SubscribeForm source="homepage" variant="light" className="w-full md:w-[420px]" />
        </div>
      </section>
    </div>
  );
};

export default Homepage;